import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || '/api';

const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json'
  }
});

// Attach auth headers on every request:
// - web2 users: Authorization: Bearer <jwt>
// - Keychain users: X-Hive-Username: <username>
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('hive_web2_token');
  if (token) {
    config.headers['Authorization'] = `Bearer ${token}`;
  } else {
    const username = localStorage.getItem('hive_username');
    if (username) {
      config.headers['X-Hive-Username'] = username;
    }
  }
  return config;
});

export const postsApi = {
  // Get global feed
  getGlobalFeed: async (limit = 20, offset = 0, viewer = null, sort = 'created') => {
    const params = { limit, offset, sort };
    if (viewer) params.viewer = viewer;
    
    const response = await api.get('/feed/global', { params });
    return response.data;
  },

  // Get trending posts
  getTrendingFeed: async (limit = 20, offset = 0, viewer = null) => {
    const params = { limit, offset };
    if (viewer) params.viewer = viewer;
    
    const response = await api.get('/feed/trending', { params });
    return response.data;
  },

  // Get posts from specific feeds (threads, snaps, waves, hangs)
  getFeedPosts: async (feedType, limit = 20, offset = 0, viewer = null, sort = 'created') => {
    const params = { limit, offset, sort };
    if (viewer) params.viewer = viewer;
    
    const response = await api.get(`/feed/${feedType}`, { params });
    return response.data;
  },

  // Get a specific post (optionally without comments)
  getPost: async (author, permlink, includeComments = true) => {
    const params = { includeComments: includeComments ? 'true' : 'false' };
    const response = await api.get(`/posts/${author}/${permlink}`, { params });
    return response.data;
  },

  // create a draft/pending record before broadcasting
  createDraft: async (payload) => {
    const response = await api.post('/posts/draft', payload);
    return response.data;
  },

  // update pending status (called after blockchain broadcast)
  updateStatus: async (payload) => {
    const response = await api.post('/posts/status', payload);
    return response.data;
  },

  // Get comments for a post
  getPostComments: async (author, permlink) => {
    const response = await api.get(`/posts/${author}/${permlink}/comments`);
    return response.data;
  },

  // Get post and ALL comments (nested tree)
  getPostAll: async (author, permlink, source = 'auto') => {
    const response = await api.get(`/posts/${author}/${permlink}/all`, { params: { source } });
    return response.data;
  },

  // Get posts by user
  getUserPosts: async (username, limit = 20) => {
    const response = await api.get(`/posts/user/${username}`, { params: { limit } });
    return response.data;
  },

  // Get short posts by user from specific feed
  getUserShortPostsByFeed: async (username, feedType, limit = 20, offset = 0, startPermlink = null, source = 'db', postFilter = 'posts') => {
    const params = { limit, offset, source, postFilter };
    if (startPermlink) params.startPermlink = startPermlink;
    
    const response = await api.get(`/posts/user/${username}/short/${feedType}`, { params });
    return response.data;
  },

  // Get blog posts by user (long-form content)
  getUserBlogPosts: async (username, limit = 10, includeReblogs = true, startPermlink = null, reblogsOnly = false, blogType = 'posts', source = 'auto') => {
    const params = {
      limit,
      includeReblogs: includeReblogs.toString(),
      reblogsOnly: reblogsOnly.toString(),
      blogType,
      source,
    };
    if (startPermlink) params.startPermlink = startPermlink;
    
    const response = await api.get(`/posts/user/${username}/blog`, { params });
    return response.data;
  },

  // Sync latest on-chain profile content into database
  syncUserProfileContent: async (username) => {
    const response = await api.post(`/posts/user/${username}/sync-profile-content`);
    return response.data;
  },

  // Get feed for user (posts from followed accounts)
  getUserFeed: async (username, limit = 20, offset = 0) => {
    const params = { username, limit, offset };
    
    const response = await api.get('/feed/home', { params });
    return response.data;
  },

  // Get posts by hashtag (searches both short-form and long-form)
  getPostsByTag: async (tag, limit = 100, offset = 0, viewer = null) => {
    const params = { limit, offset };
    if (viewer) params.viewer = viewer;
    
    const response = await api.get(`/posts/tag/${tag}`, { params });
    return response.data;
  },

  // Get short posts by hashtag within a specific feed (body hashtag search)
  getFeedPostsByTag: async (feedType, tag, limit = 100, offset = 0, viewer = null) => {
    const params = { limit, offset };
    if (viewer) params.viewer = viewer;

    const response = await api.get(`/feed/${feedType}/tag/${encodeURIComponent(tag)}`, { params });
    return response.data;
  },

  // Optimistically upsert short post into database
  upsertOptimisticShortPost: async (payload) => {
    const response = await api.post('/posts/optimistic', payload);
    return response.data;
  },

  // Get articles feed with sort and app filter
  // sort: 'created' | 'trending' | 'following' | 'subscriptions'
  getArticlesFeed: async (limit = 20, offset = 0, sort = 'created', app = 'flexus', observer = null) => {
    const params = { limit, offset, sort, app };
    if (observer) params.observer = observer;
    const response = await api.get('/feed/articles', { params });
    return response.data;
  },

  // Fetch community metadata (uses feed route)
  getCommunityInfo: async (slug) => {
    const response = await api.get(`/feed/c/${slug}/about`);
    return response.data;
  },
};

// --- Workspace API (drafts, templates, scheduled articles) --------------------
export const workspaceApi = {
  // Drafts
  listDrafts: (author) =>
    api.get('/workspace/drafts', { params: { author } }).then(r => r.data),
  getDraft: (id, author) =>
    api.get(`/workspace/drafts/${id}`, { params: { author } }).then(r => r.data),
  createDraft: (payload) =>
    api.post('/workspace/drafts', payload).then(r => r.data),
  updateDraft: (id, payload) =>
    api.put(`/workspace/drafts/${id}`, payload).then(r => r.data),
  deleteDraft: (id, author) =>
    api.delete(`/workspace/drafts/${id}`, { params: { author } }).then(r => r.data),

  // Templates
  listTemplates: (author) =>
    api.get('/workspace/templates', { params: { author } }).then(r => r.data),
  getTemplate: (id, author) =>
    api.get(`/workspace/templates/${id}`, { params: { author } }).then(r => r.data),
  createTemplate: (payload) =>
    api.post('/workspace/templates', payload).then(r => r.data),
  updateTemplate: (id, payload) =>
    api.put(`/workspace/templates/${id}`, payload).then(r => r.data),
  deleteTemplate: (id, author) =>
    api.delete(`/workspace/templates/${id}`, { params: { author } }).then(r => r.data),

  // Scheduled articles
  listScheduled: (author) =>
    api.get('/workspace/scheduled', { params: { author } }).then(r => r.data),
  createScheduled: (payload) =>
    api.post('/workspace/scheduled', payload).then(r => r.data),
  reschedule: (id, payload) =>
    api.put(`/workspace/scheduled/${id}/reschedule`, payload).then(r => r.data),
  cancelScheduled: (id, author) =>
    api.put(`/workspace/scheduled/${id}/cancel`, { author }).then(r => r.data),
  postNow: (id, author) =>
    api.put(`/workspace/scheduled/${id}/post-now`, { author }).then(r => r.data),
};

export const userApi = {
  // Get user profile
  getProfile: async (username) => {
    const response = await api.get(`/user/${username}`);
    return response.data;
  },






  // Get followers
  getFollowers: async (username, limit = 100) => {
    const response = await api.get(`/user/${username}/followers`, {
      params: { limit }
    });
    return response.data;
  },

  // Get following
  getFollowing: async (username, limit = 100) => {
    const response = await api.get(`/user/${username}/following`, {
      params: { limit }
    });
    return response.data;
  },

  // Check whether `follower` follows `target` — DB-backed, no stale cache
  checkFollows: async (follower, target) => {
    const response = await api.get(`/user/${encodeURIComponent(follower)}/follows/${encodeURIComponent(target)}`);
    return response.data; // { isFollowing: boolean, source: 'db'|'chain'|'unknown' }
  },

  // Check whether `username` is subscribed to `community` — DB-backed, no stale cache
  checkSubscription: async (username, community) => {
    const response = await api.get(`/user/${encodeURIComponent(username)}/subscriptions/${encodeURIComponent(community)}`);
    return response.data; // { isSubscribed: boolean, source: 'db'|'chain'|'unknown' }
  },

  // Refresh subscriptions cache in the background
  refreshSubscriptions: async (username) => {
    const response = await api.post(`/user/${encodeURIComponent(username)}/subscriptions/refresh`, { force: true });
    return response.data;
  },

  // Stored posting key (for Keychain users opting in to key storage for scheduling)
  getStoredPostingKeyStatus: (username) =>
    api.get(`/user/${username}/stored-posting-key/status`).then(r => r.data),
  storePostingKey: (username, posting_key) =>
    api.post(`/user/${username}/stored-posting-key`, { posting_key }).then(r => r.data),
  clearStoredPostingKey: (username) =>
    api.delete(`/user/${username}/stored-posting-key`).then(r => r.data),
};

export const listsApi = {
  getLists: () =>
    api.get('/lists').then(r => r.data),
  createList: (name) =>
    api.post('/lists', { name }).then(r => r.data),
  renameList: (listId, name) =>
    api.put(`/lists/${listId}`, { name }).then(r => r.data),
  deleteList: (listId) =>
    api.delete(`/lists/${listId}`).then(r => r.data),
  addMember: (listId, username) =>
    api.post(`/lists/${listId}/members`, { username }).then(r => r.data),
  removeMember: (listId, username) =>
    api.delete(`/lists/${listId}/members/${username}`).then(r => r.data),
  getListFeed: (listId, limit = 20, offset = 0) =>
    api.get(`/lists/${listId}/feed`, { params: { limit, offset } }).then(r => r.data),
  getListArticlesFeed: (listId, limit = 20, offset = 0, sort = 'created', app = 'all') =>
    api.get(`/lists/${listId}/articles`, { params: { limit, offset, sort, app } }).then(r => r.data),
};

export const bookmarksApi = {
  getFolders: () =>
    api.get('/bookmarks').then(r => r.data),
  createFolder: (name) =>
    api.post('/bookmarks', { name }).then(r => r.data),
  renameFolder: (folderId, name) =>
    api.put(`/bookmarks/${folderId}`, { name }).then(r => r.data),
  deleteFolder: (folderId) =>
    api.delete(`/bookmarks/${folderId}`).then(r => r.data),
  addBookmark: (folderId, author, permlink) =>
    api.post(`/bookmarks/${folderId}/items`, { author, permlink }).then(r => r.data),
  removeBookmark: (folderId, author, permlink) =>
    api.delete(`/bookmarks/${folderId}/items`, { data: { author, permlink } }).then(r => r.data),
  getFeed: (folderId, limit = 20, offset = 0, search = '') =>
    api.get(`/bookmarks/${folderId}/feed`, { params: { limit, offset, search } }).then(r => r.data),
};

export const autovoteApi = {
  getRules: (username) =>
    api.get(`/autovote/${username}/rules`).then(r => r.data),
  saveRule: (username, payload) =>
    api.post(`/autovote/${username}/rules`, payload).then(r => r.data),
  deleteRule: (username, ruleId) =>
    api.delete(`/autovote/${username}/rules/${ruleId}`).then(r => r.data),
  toggleRulePause: (username, ruleId, is_paused) =>
    api.patch(`/autovote/${username}/rules/${ruleId}/pause`, { is_paused }).then(r => r.data),
  getHistory: (username, offset = 0, limit = 50) =>
    api.get(`/autovote/${username}/history`, { params: { offset, limit } }).then(r => r.data),
  getStats: (username) =>
    api.get(`/autovote/${username}/stats`).then(r => r.data),
  retryAutovote: (username, logId) =>
    api.post(`/autovote/${username}/retry/${logId}`).then(r => r.data),
  retryAllAutovotes: (username) =>
    api.post(`/autovote/${username}/retry-all`).then(r => r.data),
};

export const pollsApi = {
  getPoll: (author, permlink, viewer = null) =>
    api.get(`/polls/${author}/${permlink}`, { params: { viewer } }).then(r => r.data),
  upsertPoll: (author, permlink, payload) =>
    api.post(`/polls/${author}/${permlink}/upsert`, payload).then(r => r.data),
  vote: (author, permlink, choices) =>
    api.post(`/polls/${author}/${permlink}/vote`, { choices }).then(r => r.data),
};

export default api;
